import type { UseQueryResult } from '@tanstack/react-query';

import { useQuery } from '@tanstack/react-query';

import type {
  GetUsersRequest,
  GetUsersResult,
} from '../../api/get-users/getUsers.types';
import type { UseUsersParams } from './useUsers.types';

import { getUsers } from '../../api/get-users/getUsers';
import { buildSortParam } from './useUsers.utils';

export function useUsers(
  params: UseUsersParams
): UseQueryResult<GetUsersResult, Error> {
  const sortParam =
    params.sortBy && params.sortDirection
      ? buildSortParam(params.sortBy, params.sortDirection)
      : undefined;

  const apiParams: GetUsersRequest = {
    organizationId: params.organizationId,
    top: params.pageSize,
    skip: params.pageIndex * params.pageSize,
  };

  if (params.searchText) apiParams.searchText = params.searchText;
  if (sortParam) apiParams.sort = sortParam;
  if (params.status?.length) apiParams.status = params.status;
  if (params.roles?.length) apiParams.roles = params.roles;

  const queryKey = [
    'users',
    'data',
    params.organizationId,
    {
      pagination: {
        pageSize: params.pageSize,
        pageIndex: params.pageIndex,
      },
      sorting: {
        sortBy: params.sortBy ?? null,
        sortDirection: params.sortDirection ?? null,
      },
      filters: {
        searchText: params.searchText ?? null,
        status: params.status ?? null,
        roles: params.roles ?? null,
      },
    },
  ];

  return useQuery<GetUsersResult, Error>({
    queryKey,
    queryFn: (): Promise<GetUsersResult> => getUsers(apiParams),
    enabled: !!params.organizationId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
